import {ResponseType} from './types';
import {loadTokens, saveTokens} from '../utils/storage';
import {validateAndHandleUserStatus} from '../utils/userStatusHandler';
import {StatusCodes} from 'http-status-codes';

const API_HEADERS = {
    CONTENT_TYPE: 'Content-Type',
    AUTHORIZATION: 'Authorization',
} as const;

const API_CONTENT_TYPES = {
    JSON: 'application/json',
} as const;

const API_BASE_URL = process.env.REACT_APP_API;

const getAuthToken = () => loadTokens()?.accessToken;
const getRefreshToken = () => loadTokens()?.refreshToken;

let isRefreshing = false;
// 토큰 만료로 재발급시 대기 중인 요청 큐
let failedRequestsQueue: {
    resolve: (value: ResponseType | PromiseLike<ResponseType>) => void;
    reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any | null = null) => {
    failedRequestsQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(true as unknown as ResponseType);
        }
    });
    failedRequestsQueue = [];
};

// 응답 데이터 처리 공통 함수
const handleResponse = (responseData: any) => {
    const {isSuccess, code, message, result} = responseData;
    return {isSuccess, code, message, result};
};

const request = async <T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    endPoint: string,
    useToken: boolean,
    data?: T,
    isMultipart?: boolean,
): Promise<ResponseType<T>> => {
    let url = `${API_BASE_URL}${endPoint}`;
    const headers = new Headers({});

    if (method === 'GET' && data) {
        const queryStringData: Record<string, string> = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                queryStringData[key] = String((data as any)[key]);
            }
        }
        const queryParams = new URLSearchParams(queryStringData).toString();
        url = `${url}?${queryParams}`;
    } else if (!isMultipart) {
        headers.append(API_HEADERS.CONTENT_TYPE, API_CONTENT_TYPES.JSON);
    }

    if (useToken) {
        const accessToken = getAuthToken();
        if (accessToken) {
            headers.append(API_HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
        }
    }

    const fetchOptions: RequestInit = {
        method,
        headers,
    };

    if (method !== 'GET' && data) {
        fetchOptions.body = isMultipart ? (data as unknown as FormData) : JSON.stringify(data);
        if (isMultipart) {
            headers.delete(API_HEADERS.CONTENT_TYPE);
        }
    }

    try {
        const response = await fetch(url, fetchOptions);

        // 413 에러 처리 - Nginx에서 파일 크기 초과 시 반환
        if (response.status === 413) {
            return handleResponse({
                isSuccess: false,
                code: 413,
                message: '파일 크기가 너무 큽니다. 총 50MB 이하로만 업로드 가능합니다.',
                result: null,
            });
        }

        const responseData = await response.json();

        if (responseData.code === StatusCodes.UNAUTHORIZED && useToken) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedRequestsQueue.push({
                        resolve: () => resolve(request(method, endPoint, useToken, data, isMultipart)),
                        reject,
                    });
                });
            }

            isRefreshing = true;
            try {
                const currentRefreshToken = getRefreshToken();
                if (!currentRefreshToken) {
                    console.error('에러 발생: 리프레시 토큰이 없습니다.');
                    throw new Error('다시 로그인해주세요.');
                }

                const refreshResponse = await fetch(`${API_BASE_URL}/api/authentication/issue-token`, {
                    method: 'POST',
                    headers: {
                        [API_HEADERS.CONTENT_TYPE]: API_CONTENT_TYPES.JSON,
                    },
                    body: JSON.stringify({refreshToken: currentRefreshToken}),
                });

                const refreshData = await refreshResponse.json();

                if (refreshData.code === StatusCodes.OK && refreshData.isSuccess) {
                    const newAccessToken = refreshData.result.accessToken;
                    const newRefreshToken = refreshData.result.refreshToken;

                    saveTokens({accessToken: newAccessToken, refreshToken: newRefreshToken});

                    // 토큰 갱신 후 사용자 상태 확인
                    try {
                        const userResponse = await fetch(`${API_BASE_URL}/api/users/me`, {
                            method: 'GET',
                            headers: {
                                [API_HEADERS.AUTHORIZATION]: `Bearer ${newAccessToken}`,
                                [API_HEADERS.CONTENT_TYPE]: API_CONTENT_TYPES.JSON,
                            },
                        });

                        const userData = await userResponse.json();
                        if (userData.code === StatusCodes.OK && userData.isSuccess && userData.result) {
                            // 토큰 갱신 시에는 네비게이션 없이 상태만 확인
                            await validateAndHandleUserStatus(userData.result, undefined);
                        }
                    } catch (userCheckError) {
                        console.warn('토큰 갱신 후 사용자 상태 확인 중 오류:', userCheckError);
                        // 사용자 상태 확인 실패는 !== 토큰 갱신 실패
                    }

                    isRefreshing = false;
                    processQueue();

                    const retryHeaders = new Headers(headers);
                    retryHeaders.set(API_HEADERS.AUTHORIZATION, `Bearer ${newAccessToken}`);

                    return await request(method, endPoint, useToken, data, isMultipart);
                } else {
                    throw new Error(refreshData.message);
                }
            } catch (refreshError: any) {
                console.error('Failed to refresh token:', refreshError);
                isRefreshing = false;
                processQueue(refreshError);

                return handleResponse({
                    isSuccess: false,
                    code: StatusCodes.UNAUTHORIZED,
                    message: '',
                    result: null,
                });
            }
        }

        return handleResponse(responseData);
    } catch (err: unknown) {
        if (err instanceof Error) {
            throw new Error(`API 요청 실패: ${err.message}`);
        }
        throw new Error('알 수 없는 에러 발생');
    }
};

const requestImage = async (method: 'GET', data?: any): Promise<Blob> => {
    let url = `${API_BASE_URL}/api/image`;
    const headers = new Headers({});

    if (method === 'GET' && data) {
        const queryStringData: Record<string, string> = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                queryStringData[key] = String(data[key]);
            }
        }
        const queryParams = new URLSearchParams(queryStringData).toString();
        url = `${url}?${queryParams}`;
    }

    const accessToken = getAuthToken();
    if (accessToken) {
        headers.append(API_HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
    }

    const fetchOptions: RequestInit = {
        method,
        headers,
    };

    try {
        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.blob();
    } catch (err: unknown) {
        if (err instanceof Error) {
            throw new Error(`이미지 API 요청 실패: ${err.message}`);
        }
        throw new Error('알 수 없는 에러 발생');
    }
};

export const requests = {
    get: (endPoint: string, useToken: boolean, query?: Record<string, string | number | boolean>) =>
        request('GET', endPoint, useToken, query),
    post: <T>(endPoint: string, useToken: boolean, body?: T, isMultipart?: boolean) =>
        request('POST', endPoint, useToken, body, isMultipart),
    put: (endPoint: string, useToken: boolean, body?: any, isMultipart?: boolean) =>
        request('PUT', endPoint, useToken, body, isMultipart),
    patch: (endPoint: string, useToken: boolean, body?: any, isMultipart?: boolean) =>
        request('PATCH', endPoint, useToken, body, isMultipart),
    delete: (endPoint: string, useToken: boolean, body?: any) => request('DELETE', endPoint, useToken, body),
    getImage: (query?: Record<string, string | number | boolean>) => requestImage('GET', query),
};
